"use client";

import { CenterOptions } from "@/Helpers/CenterOptions";
import { getSectionDefaultProps } from "@/Templates/Doccure/getSectionDefaultProps";
import { AboutSectionProps } from "@/Templates/SectionsProps";
import { Icon } from "@iconify/react";
import {
  cloneElement,
  FC,
  isValidElement,
  ReactElement,
  ReactNode,
} from "react";
import HomeEightAboutSectionGenerated, {
  HomeEightAboutSectionGeneratedProps,
} from "../Sections/_generated/HomeEightAboutSection.generated";
import HomeEighteenAboutSectionGenerated, {
  HomeEighteenAboutSectionGeneratedProps,
} from "../Sections/_generated/HomeEighteenAboutSection.generated";
import HomeElevenAboutSectionGenerated, {
  HomeElevenAboutSectionGeneratedProps,
} from "../Sections/_generated/HomeElevenAboutSection.generated";
import HomeFifteenAboutusSectionGenerated, {
  HomeFifteenAboutusSectionGeneratedProps,
} from "../Sections/_generated/HomeFifteenAboutusSection.generated";
import HomeFiveAboutSectionGenerated, {
  HomeFiveAboutSectionGeneratedProps,
} from "../Sections/_generated/HomeFiveAboutSection.generated";
import HomeFourAboutSectionGenerated, {
  HomeFourAboutSectionGeneratedProps,
} from "../Sections/_generated/HomeFourAboutSection.generated";
import HomeFourteenAboutSectionGenerated, {
  HomeFourteenAboutSectionGeneratedProps,
} from "../Sections/_generated/HomeFourteenAboutSection.generated";
import HomeNineAboutSectionGenerated, {
  HomeNineAboutSectionGeneratedProps,
} from "../Sections/_generated/HomeNineAboutSection.generated";
import HomeSevenAboutSectionGenerated, {
  HomeSevenAboutSectionGeneratedProps,
} from "../Sections/_generated/HomeSevenAboutSection.generated";
import HomeSeventeenAboutSectionGenerated, {
  HomeSeventeenAboutSectionGeneratedProps,
} from "../Sections/_generated/HomeSeventeenAboutSection.generated";
import HomeSixAboutusSectionGenerated, {
  HomeSixAboutusSectionGeneratedProps,
} from "../Sections/_generated/HomeSixAboutusSection.generated";
import HomeSixteenAboutUsGenerated, {
  HomeSixteenAboutUsGeneratedProps,
} from "../Sections/_generated/HomeSixteenAboutUs.generated";
import HomeTenAboutSectionGenerated, {
  HomeTenAboutSectionGeneratedProps,
} from "../Sections/_generated/HomeTenAboutSection.generated";
import HomeThreeAboutSectionGenerated, {
  HomeThreeAboutSectionGeneratedProps,
} from "../Sections/_generated/HomeThreeAboutSection.generated";
import HomeTwelveAboutSectionGenerated, {
  HomeTwelveAboutSectionGeneratedProps,
} from "../Sections/_generated/HomeTwelveAboutSection.generated";
import HomeTwoAboutSectionGenerated, {
  HomeTwoAboutSectionGeneratedProps,
} from "../Sections/_generated/HomeTwoAboutSection.generated";
import { renderGeneratedSection } from "./generated-section-renderer";
import useDoccureVideoModal from "./useDoccureVideoModal";

type GeneratedAboutRenderer = (props: AboutSectionProps) => ReactElement;
type GeneratedAboutProps = Record<string, unknown>;
type GeneratedAboutItem = Record<string, unknown>;
type AboutSettingRecord = Record<string, unknown>;
type AboutHighlightItem = {
  Id: number;
  Text?: string;
};

const AboutVideoButtonRenderer: FC<{
  src: string;
  defaultNode: ReactNode;
}> = ({ src, defaultNode }) => {
  const videoModal = useDoccureVideoModal();

  if (!isValidElement(defaultNode)) {
    return defaultNode;
  }

  return cloneElement(
    defaultNode as ReactElement<{
      onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    }>,
    {
      onClick: (event) => {
        event.preventDefault();
        event.stopPropagation();
        videoModal.show(src);
      },
    },
  );
};

function buildGeneratedAboutProps<T extends Record<string, unknown>>(
  sectionKey: string,
  overrides?: Partial<T>,
): T {
  return getSectionDefaultProps<T>(sectionKey, {
    ...(overrides ?? {}),
  } as Partial<T>);
}

function createAboutTitleRenderer(title?: string) {
  if (!title?.trim()) {
    return undefined;
  }

  return (defaultNode: ReactNode) =>
    isValidElement(defaultNode)
      ? cloneElement(defaultNode, undefined, title)
      : title;
}

const titleKeyMatchers = [
  /H20H2Text$/,
  /H40H4Text$/,
  /H30H3Text$/,
  /h30Text$/,
  /Link0Text$/,
];

const subtitleKeyMatchers = [/Div0DivText$/, /DivText$/, /Span0SpanText$/];

const headingSpanKeyMatchers = [/H20Span0SpanText$/, /H20Span1SpanText$/];

const descriptionKeyMatchers = [/P0PText$/, /P1PText$/, /p0Text$/];

const buttonHrefKeyMatchers = [/Link0LinkHref$/];

const buttonTextKeyMatchers = [/Link0LinkText$/, /Link0Span0SpanText$/];

const buttonVisibleKeyMatchers = [/Link0LinkVisible$/];

const imageSrcKeyMatchers = [
  /src$/,
  /ImageWithBasePath0ImageSrc$/,
  /ImageWithBasePath1ImageSrc$/,
  /ImageWithBasePath2ImageSrc$/,
  /ImageWithBasePath3ImageSrc$/,
];

function findFirstMatchingKey(
  target: GeneratedAboutProps | GeneratedAboutItem,
  matchers: RegExp[],
): string | undefined {
  return Object.keys(target).find((key) =>
    matchers.some((matcher) => matcher.test(key)),
  );
}

function setFirstMatchingValue(
  target: GeneratedAboutProps | GeneratedAboutItem,
  matchers: RegExp[],
  value: unknown,
): void {
  if (value === null || value === undefined || value === "") {
    return;
  }

  const key = findFirstMatchingKey(target, matchers);
  if (!key) {
    return;
  }

  target[key] = value;
}

function setFirstMatchingValueAllowEmpty(
  target: GeneratedAboutProps | GeneratedAboutItem,
  matchers: RegExp[],
  value: unknown,
): void {
  if (value === null || value === undefined) {
    return;
  }

  const key = findFirstMatchingKey(target, matchers);
  if (!key) {
    return;
  }

  target[key] = value;
}

function isImageLikeValue(value?: string | null): value is string {
  if (!value) {
    return false;
  }

  return (
    value.startsWith("/") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  );
}

function getAboutSettingImageUrl(
  setting: AboutSectionProps["setting"],
  key: "Image1" | "Image2" | "VideoImage" | "Video",
): string | undefined {
  const value = (setting as unknown as AboutSettingRecord)[key];
  if (!value || typeof value !== "object") {
    return undefined;
  }

  const url = (value as Record<string, unknown>).Url;
  return typeof url === "string" && url.length > 0 ? url : undefined;
}

function getPrimaryAboutImageUrl(
  setting: AboutSectionProps["setting"],
): string | undefined {
  return getAboutSettingImageUrl(setting, "Image1");
}

function getSecondaryAboutImageUrl(
  setting: AboutSectionProps["setting"],
): string | undefined {
  return getAboutSettingImageUrl(setting, "Image2");
}

function renderIconNode(
  icon?: string | null,
  className = "img-fluid",
): ReactElement | null {
  if (!icon) {
    return null;
  }

  if (isImageLikeValue(icon)) {
    return <img src={icon} className={className} alt="" />;
  }

  return <Icon icon={icon} className={className} />;
}

function getAboutListItems(props: AboutSectionProps) {
  return props.lists?.Default ?? [];
}

function getAboutHighlights(props: AboutSectionProps) {
  return props.lists?.AboutHighlights ?? [];
}

function applyListItemOverrides(
  defaults: GeneratedAboutItem[],
  props: AboutSectionProps,
): GeneratedAboutItem[] {
  const listItems = getAboutListItems(props);
  if (listItems.length === 0 || defaults.length === 0) {
    return defaults;
  }

  return listItems.map((listItem, index) => {
    const baseItem = defaults[index % defaults.length] ?? {};
    const nextItem: GeneratedAboutItem = {
      ...baseItem,
      key: listItem.Id,
    };

    setFirstMatchingValue(nextItem, titleKeyMatchers, listItem.Title);
    setFirstMatchingValue(
      nextItem,
      descriptionKeyMatchers,
      listItem.Description,
    );

    if (isImageLikeValue(listItem.Icon)) {
      setFirstMatchingValue(nextItem, imageSrcKeyMatchers, listItem.Icon);
    }

    return nextItem;
  });
}

function applyAboutOverrides<T extends GeneratedAboutProps>(
  generatedProps: T,
  props: AboutSectionProps,
): T {
  const nextProps: GeneratedAboutProps = {
    ...generatedProps,
  };

  setFirstMatchingValue(nextProps, titleKeyMatchers, props.setting.Title);
  setFirstMatchingValueAllowEmpty(
    nextProps,
    headingSpanKeyMatchers,
    props.setting.Subtitle ?? "",
  );
  setFirstMatchingValue(nextProps, subtitleKeyMatchers, props.setting.Subtitle);
  setFirstMatchingValue(
    nextProps,
    descriptionKeyMatchers,
    props.setting.Description,
  );
  setFirstMatchingValue(
    nextProps,
    buttonHrefKeyMatchers,
    props.setting.ButtonLink,
  );
  setFirstMatchingValue(
    nextProps,
    buttonTextKeyMatchers,
    props.setting.ButtonText,
  );

  const buttonVisibleKey = findFirstMatchingKey(
    nextProps,
    buttonVisibleKeyMatchers,
  );
  if (buttonVisibleKey) {
    nextProps[buttonVisibleKey] =
      props.setting.ButtonDisplayStatus.Key ===
      CenterOptions.Enum.Status.Active.key;
  }

  const primaryImageUrl = getPrimaryAboutImageUrl(props.setting);
  if (primaryImageUrl) {
    setFirstMatchingValue(nextProps, imageSrcKeyMatchers, primaryImageUrl);
  }

  for (const key of Object.keys(nextProps)) {
    if (key === "lightboxSlides") {
      continue;
    }

    const value = nextProps[key];
    if (
      !Array.isArray(value) ||
      value.length === 0 ||
      typeof value[0] !== "object" ||
      value[0] === null
    ) {
      continue;
    }

    nextProps[key] = applyListItemOverrides(
      value as GeneratedAboutItem[],
      props,
    );
  }

  return nextProps as T;
}

const AboutAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateAboutAppearance;

const generatedAboutRenderers: Record<string, GeneratedAboutRenderer> = {
  [AboutAppearance.Home2.key]: (props) => {
    const listItems = getAboutListItems(props);
    const listItemMap = new Map(
      listItems.map((listItem) => [String(listItem.Id), listItem] as const),
    );
    const aboutVideo = getAboutSettingImageUrl(props.setting, "Video");
    const aboutVideoEnabled =
      props.setting.VideoDisplayStatus?.Key ===
        CenterOptions.Enum.Status.Active.key && Boolean(aboutVideo);

    return (
      <HomeTwoAboutSectionGenerated
        {...applyAboutOverrides(
          buildGeneratedAboutProps<HomeTwoAboutSectionGeneratedProps>(
            "HomeTwoAboutSection",
          ),
          props,
        )}
        sectionDiv0Div0Div1Div0Link0LinkVisible={
          props.setting.ButtonDisplayStatus?.Key ===
          CenterOptions.Enum.Status.Active.key
        }
        sectionDiv0Div0Div0Div0Button0ButtonVisible={aboutVideoEnabled}
        sectionDiv0Div0Div0Div0Button0ButtonRenderer={
          aboutVideoEnabled
            ? (defaultNode) => (
                <AboutVideoButtonRenderer
                  src={aboutVideo!}
                  defaultNode={defaultNode}
                />
              )
            : undefined
        }
        sectionDiv0Div0Div1Div0Div0Div0DivText={props.setting.Subtitle}
        sectionDiv0Div0Div1Div0Div0H20Span0SpanText=""
        sectionDiv0Div0Div1Div0Div0H20H2Renderer={(defaultNode) => {
          if (!props.setting.Title) {
            return defaultNode;
          }

          return <h2 className="section-title">{props.setting.Title}</h2>;
        }}
        aboutSection6sImageRenderer={(item, defaultNode) => {
          const listItem = listItemMap.get(String(item.key));
          if (!listItem) return <></>;
          if (!listItem.Icon || isImageLikeValue(listItem.Icon)) {
            return defaultNode;
          }

          return renderIconNode(
            listItem.Icon,
            item.imageWithBasePath0ClassName ?? "img-fluid",
          );
        }}
      />
    );
  },
  [AboutAppearance.Home3.key]: (props) => (
    <HomeThreeAboutSectionGenerated
      {...applyAboutOverrides(
        buildGeneratedAboutProps<HomeThreeAboutSectionGeneratedProps>(
          "HomeThreeAboutSection",
        ),
        props,
      )}
    />
  ),
  [AboutAppearance.Home4.key]: (props) => {
    const listItems = getAboutListItems(props);
    const missionItem = listItems[0] ?? {};
    const bulletItems = listItems.slice(1);
    const generatedProps = applyAboutOverrides(
      buildGeneratedAboutProps<HomeFourAboutSectionGeneratedProps>(
        "HomeFourAboutSection",
        {
          sectionDiv0Div0Div0Div0Div0ImageWithBasePath0ImageSrc:
            getPrimaryAboutImageUrl(props.setting),
          sectionDiv0Div0Div0Div0Div1ImageWithBasePath0ImageSrc:
            getSecondaryAboutImageUrl(props.setting),
          sectionDiv0Div0Div1Div0Div0Div0DivText: props.setting.Subtitle,
          sectionDiv0Div0Div1Div0Div0P0PText: props.setting.Description,
          sectionDiv0Div0Div1Div0Link0LinkHref: props.setting.ButtonLink,
          sectionDiv0Div0Div1Div0Link0LinkText: props.setting.ButtonText,
          sectionDiv0Div0Div1Div0Link0LinkVisible:
            props.setting.ButtonDisplayStatus.Key ===
            CenterOptions.Enum.Status.Active.key,
          sectionDiv0Div0Div1Div0Div1Div1H30H3Text: missionItem.Title,
          sectionDiv0Div0Div1Div0Div1Div1P0PText: missionItem.Description,
        },
      ),
      props,
    );

    return (
      <HomeFourAboutSectionGenerated
        {...generatedProps}
        sectionDiv0Div0Div0Div0H20H2Visible={false}
        sectionDiv0Div0Div1Div0Div0H20H2Renderer={(defaultNode) => {
          if (!props.setting.Title) {
            return defaultNode;
          }

          return <h2 className="section-title">{props.setting.Title}</h2>;
        }}
        sectionDiv0Div0Div1Div0Div1Div0ImageWithBasePath0ImageRenderer={(
          defaultNode,
        ) => {
          const iconNode = renderIconNode(missionItem.Icon);
          return iconNode ?? defaultNode;
        }}
        aboutSection6sRenderer={() => (
          <>
            {bulletItems.map((item) => {
              const text = item.Title ?? item.Description;
              if (!text) {
                return null;
              }

              return (
                <div className="col-md-6" key={item.Id}>
                  <ul className="about-list">
                    <li>{text}</li>
                  </ul>
                </div>
              );
            })}
          </>
        )}
      />
    );
  },
  [AboutAppearance.Home5.key]: (props) => (
    <HomeFiveAboutSectionGenerated
      {...applyAboutOverrides(
        buildGeneratedAboutProps<HomeFiveAboutSectionGeneratedProps>(
          "HomeFiveAboutSection",
        ),
        props,
      )}
    />
  ),
  [AboutAppearance.Home6.key]: (props) => {
    const listItems = getAboutListItems(props);
    const firstItem = listItems[0] ?? {};
    const secondItem = listItems[1] ?? {};

    return (
      <HomeSixAboutusSectionGenerated
        {...applyAboutOverrides(
          buildGeneratedAboutProps<HomeSixAboutusSectionGeneratedProps>(
            "HomeSixAboutusSection",
            {
              sectionDiv0Div0Div0Div0ImageWithBasePath0ImageSrc:
                getPrimaryAboutImageUrl(props.setting),
              sectionDiv0Div0Div0Div0ImageWithBasePath1ImageSrc:
                getSecondaryAboutImageUrl(props.setting),
              sectionDiv0Div0Div1Div0Div0Div0DivText: props.setting.Subtitle,
              sectionDiv0Div0Div1Div0Div0P0PText: props.setting.Description,
              sectionDiv0Div0Div1Div0Div1Div0H30H3Text: firstItem.Title,
              sectionDiv0Div0Div1Div0Div1Div0P0PText: firstItem.Description,
              sectionDiv0Div0Div1Div0Div1Div1H30H3Text: secondItem.Title,
              sectionDiv0Div0Div1Div0Div1Div1P0PText: secondItem.Description,
            },
          ),
          props,
        )}
        sectionDiv0Div0Div1Div0Div0H20H2Renderer={(defaultNode) => {
          if (!props.setting.Title) {
            return defaultNode;
          }

          return <h2 className="section-title">{props.setting.Title}</h2>;
        }}
        sectionDiv0Div0Div1Div0Div1Div0ImageWithBasePath0ImageRenderer={(
          defaultNode,
        ) => {
          const iconNode = renderIconNode(firstItem.Icon, "img-fluid icon-one");
          return iconNode ?? defaultNode;
        }}
        sectionDiv0Div0Div1Div0Div1Div1ImageWithBasePath0ImageRenderer={(
          defaultNode,
        ) => {
          const iconNode = renderIconNode(
            secondItem.Icon,
            "img-fluid icon-one",
          );
          return iconNode ?? defaultNode;
        }}
      />
    );
  },
  [AboutAppearance.Home7.key]: (props) => (
    <HomeSevenAboutSectionGenerated
      {...applyAboutOverrides(
        buildGeneratedAboutProps<HomeSevenAboutSectionGeneratedProps>(
          "HomeSevenAboutSection",
        ),
        props,
      )}
    />
  ),
  [AboutAppearance.Home8.key]: (props) => (
    <HomeEightAboutSectionGenerated
      {...applyAboutOverrides(
        buildGeneratedAboutProps<HomeEightAboutSectionGeneratedProps>(
          "HomeEightAboutSection",
        ),
        props,
      )}
    />
  ),
  [AboutAppearance.Home9.key]: (props) => (
    <HomeNineAboutSectionGenerated
      {...applyAboutOverrides(
        buildGeneratedAboutProps<HomeNineAboutSectionGeneratedProps>(
          "HomeNineAboutSection",
        ),
        props,
      )}
    />
  ),
  [AboutAppearance.Home10.key]: (props) => (
    <HomeTenAboutSectionGenerated
      {...applyAboutOverrides(
        buildGeneratedAboutProps<HomeTenAboutSectionGeneratedProps>(
          "HomeTenAboutSection",
        ),
        props,
      )}
    />
  ),
  [AboutAppearance.Home11.key]: (props) => {
    const defaultItems = getAboutListItems(props);
    const mission = defaultItems[0];
    const vision = defaultItems[1];
    const highlights = getAboutHighlights(props);
    const aboutVideo = getAboutSettingImageUrl(
      props.setting,
      "Video",
    );
    const aboutVideoImage = getAboutSettingImageUrl(
      props.setting,
      "VideoImage",
    );
    const aboutVideoEnabled =
        props.setting.VideoDisplayStatus?.Key ===
        CenterOptions.Enum.Status.Active.key && Boolean(aboutVideo);

    const generatedProps = {
      ...applyAboutOverrides(
        buildGeneratedAboutProps<HomeElevenAboutSectionGeneratedProps>(
          "HomeElevenAboutSection",
          {
            ...(getSecondaryAboutImageUrl(props.setting)
              ? {
                  sectionDiv0Div0Div0Div0Div1ImageWithBasePath0ImageSrc:
                    getSecondaryAboutImageUrl(props.setting),
                }
              : {}),
            sectionDiv0Div0Div1Div0Div3Div0H30H3Text: mission?.Title,
            sectionDiv0Div0Div1Div0Div3Div0P0PText: mission?.Description,
            sectionDiv0Div0Div1Div0Div3Div1H30H3Text: vision?.Title,
            sectionDiv0Div0Div1Div0Div3Div1P0PText: vision?.Description,
          },
        ),
        props,
      ),
      ...(props.setting.ExperienceValue
        ? {
            sectionDiv0Div0Div0Div0Div1Div0Div0Span0SpanText:
              props.setting.ExperienceValue,
          }
        : {}),
      ...(highlights.length
        ? {
            aboutSection1s: highlights.map((item) => ({
              key: item.Id,
              spanText: item.Text ?? "",
            })),
          }
        : {}),
      ...(aboutVideoEnabled
        ? {
            sectionDiv0Div0Div0Div0Div0Link0LinkHref: aboutVideo,
            sectionDiv0Div0Div0Div0Div0Link0LinkVisible: true,
            sectionDiv0Div0Div0Div0Div0Link0Button0ButtonRenderer: (
              defaultNode: ReactNode,
            ) => (
              <AboutVideoButtonRenderer
                src={aboutVideo!}
                defaultNode={defaultNode}
              />
            ),
            ...(aboutVideoImage
              ? {
                  sectionDiv0Div0Div0Div0Div0ImageWithBasePath0ImageSrc:
                    aboutVideoImage,
                }
              : {}),
          }
        : {}),
    };

    return (
      <HomeElevenAboutSectionGenerated
        {...generatedProps}
        {...(props.setting.Title
          ? {
              sectionDiv0Div0Div1Div0Div0H20H2Renderer:
                createAboutTitleRenderer(props.setting.Title),
            }
          : {})}
        {...(props.setting.Subtitle
          ? {
              sectionDiv0Div0Div1Div0Div0Div0DivText: props.setting.Subtitle,
            }
          : {})}
        {...(props.setting.Description
          ? {
              sectionDiv0Div0Div1Div0Div2Div1DivText: props.setting.Description,
            }
          : {})}
      />
    );
  },
  [AboutAppearance.Home12.key]: (props) => (
    <HomeTwelveAboutSectionGenerated
      {...applyAboutOverrides(
        buildGeneratedAboutProps<HomeTwelveAboutSectionGeneratedProps>(
          "HomeTwelveAboutSection",
        ),
        props,
      )}
    />
  ),
  [AboutAppearance.Home14.key]: (props) => (
    <HomeFourteenAboutSectionGenerated
      {...applyAboutOverrides(
        buildGeneratedAboutProps<HomeFourteenAboutSectionGeneratedProps>(
          "HomeFourteenAboutSection",
        ),
        props,
      )}
    />
  ),
  [AboutAppearance.Home15.key]: (props) => (
    <HomeFifteenAboutusSectionGenerated
      {...applyAboutOverrides(
        buildGeneratedAboutProps<HomeFifteenAboutusSectionGeneratedProps>(
          "HomeFifteenAboutusSection",
        ),
        props,
      )}
    />
  ),
  [AboutAppearance.Home16.key]: (props) => (
    <HomeSixteenAboutUsGenerated
      {...applyAboutOverrides(
        buildGeneratedAboutProps<HomeSixteenAboutUsGeneratedProps>(
          "HomeSixteenAboutUs",
        ),
        props,
      )}
    />
  ),
  [AboutAppearance.Home17.key]: (props) => (
    <HomeSeventeenAboutSectionGenerated
      {...applyAboutOverrides(
        buildGeneratedAboutProps<HomeSeventeenAboutSectionGeneratedProps>(
          "HomeSeventeenAboutSection",
        ),
        props,
      )}
    />
  ),
  [AboutAppearance.Home18.key]: (props) => (
    <HomeEighteenAboutSectionGenerated
      {...applyAboutOverrides(
        buildGeneratedAboutProps<HomeEighteenAboutSectionGeneratedProps>(
          "HomeEighteenAboutSection",
        ),
        props,
      )}
    />
  ),
};

const GeneratedAboutSection: FC<AboutSectionProps> = (props) => {
  return renderGeneratedSection(
    props,
    generatedAboutRenderers,
    AboutAppearance.Home2.key,
    (x) => x.setting.Appearance?.Key,
  );
};

export default GeneratedAboutSection;
